import { gql, useQuery } from '@apollo/client';
import { useFlashMutation } from '@everlutionsk/ui-apollo';
import {
  Alert,
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Link,
  Stack,
  Typography
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Button } from '../components/coreComponents/button';
import { useIsSuperAdmin } from '../identityContext';

// Deep links to the seed data definitions. Line numbers come from the
// current branch — adjust here if seedWrapper.ts shifts on main.
const seedWrapperBaseUrl =
  'https://github.com/everlutionsk/asign/blob/main/packages/api/prisma/seedWrapper.ts';

const seedDefinitions = [
  { key: 'businessFunctions', label: 'Default business functions', line: 437 },
  { key: 'businessServices', label: 'Default business services', line: 486 },
  { key: 'enablingServices', label: 'Default enabling services', line: 546 },
  { key: 'basicAssets', label: 'Basic assets', line: 694 }
] as const;

type SeedKey = (typeof seedDefinitions)[number]['key'];

function githubLink(line: number): string {
  return `${seedWrapperBaseUrl}#L${line}`;
}

interface AccountConfigurationTabProps {
  readonly accountId: string;
  readonly publishedAt: string | null;
}

export function AccountConfigurationTab({ accountId, publishedAt }: AccountConfigurationTabProps) {
  const isSuperAdmin = useIsSuperAdmin();
  const isPublished = publishedAt != null;
  // Regular admins are blocked entirely once the account is published.
  // Superadmins can still seed but get an extra warning — the server
  // mirrors this rule (any tampering with the FE state hits a 403).
  const publishLock = isPublished && !isSuperAdmin;
  const { data, refetch, startPolling, stopPolling } = useQuery(seedStatusQuery, {
    variables: { accountId },
    fetchPolicy: 'cache-and-network'
  });
  const [selected, setSelected] = useState<ReadonlySet<SeedKey>>(new Set());
  const [confirmOpen, setConfirmOpen] = useState<boolean>(false);
  // Bridges the window between the mutation resolving and the refetch
  // reporting `inProgress: true`, so the Seed button never re-enables in
  // between.
  const [submitting, setSubmitting] = useState<boolean>(false);

  const [runSeed, { loading }] = useFlashMutation(seedRunMutation, {
    successMsg: 'Seeding started — this can take a few minutes.'
  });

  const status = data?.accountSeedDefaults ?? null;
  const inProgress = status?.inProgress ?? false;
  const items = status?.items ?? [];
  const locked = inProgress || loading || submitting || publishLock;

  // Poll only while a job is running; stop once it's idle.
  useEffect(() => {
    if (inProgress) startPolling(5000);
    else stopPolling();
  }, [inProgress, startPolling, stopPolling]);

  const toggle = (key: SeedKey) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const seededKeys = new Set<string>(items.filter(item => item.seeded).map(item => item.key));
  // Exclude sets that have since been seeded so a kept selection can't
  // re-enable the action for an already-seeded (locked) set.
  const selectedKeys = [...selected].filter(key => !seededKeys.has(key));
  const selectedLabels = selectedKeys.map(
    key => seedDefinitions.find(definition => definition.key === key)?.label ?? key
  );

  // Only not-yet-seeded sets can be (de)selected; seeded ones are locked on.
  const selectableKeys = items.filter(item => !item.seeded).map(item => item.key as SeedKey);
  const allSelectableSelected =
    selectableKeys.length > 0 && selectableKeys.every(key => selected.has(key));

  const toggleAll = () => {
    setSelected(() => (allSelectableSelected ? new Set() : new Set(selectableKeys)));
  };

  const handleConfirm = async () => {
    setSubmitting(true);
    setConfirmOpen(false);
    try {
      await runSeed({ variables: { accountId, keys: selectedKeys } });
    } catch {
      // useFlashMutation surfaces the error; nothing more to do here.
    } finally {
      // Refetch so `inProgress` takes over the lock, then drop the bridge
      // flag. While a job is in flight `inProgress` keeps everything disabled
      // (and the poll clears it on completion); if the enqueue failed (no job
      // created) `inProgress` is false and the controls re-enable for a retry.
      await refetch();
      setSubmitting(false);
    }
  };

  return (
    <Box>
      {isPublished && !isSuperAdmin && (
        <Alert severity="error" sx={{ mb: 4, maxWidth: 720 }}>
          This account was published on <strong>{new Date(publishedAt).toLocaleString()}</strong>{' '}
          and is now treated as production. Seeding default data is no longer available — contact a
          superadmin if you believe this is wrong.
        </Alert>
      )}

      {isPublished && isSuperAdmin && (
        <Alert severity="error" sx={{ mb: 4, maxWidth: 720 }}>
          <strong>Published account.</strong> This account is live as of{' '}
          {new Date(publishedAt).toLocaleString()}. Seeding here will write to a production tenant
          and is <strong>irreversible</strong>. Proceed only if you are certain.
        </Alert>
      )}

      <Alert severity="warning" sx={{ mb: 4, maxWidth: 720 }}>
        Seeding default data is <strong>irreversible</strong>. Once a data set has been seeded into
        this account it cannot be removed, and it can only be seeded once.
      </Alert>

      {inProgress && (
        <Alert severity="info" sx={{ mb: 4, maxWidth: 720 }}>
          A seeding job is currently running for this account. The list updates automatically when
          it finishes.
        </Alert>
      )}

      <Stack direction="row" justifyContent="flex-end" sx={{ maxWidth: 720, mb: 2 }}>
        <Button
          variant="secondary"
          disabled={locked || selectableKeys.length === 0}
          onClick={toggleAll}
        >
          {allSelectableSelected ? 'Deselect all' : 'Select all'}
        </Button>
      </Stack>

      <Stack spacing={2} sx={{ maxWidth: 720 }}>
        {items.map(item => {
          const definition = seedDefinitions.find(entry => entry.key === item.key);
          const isChecked = item.seeded || selected.has(item.key as SeedKey);
          return (
            <Box
              key={item.key}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 3,
                borderRadius: 1,
                border: theme => `1px solid ${theme.brandPalette.border.neutral}`
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isChecked}
                    disabled={item.seeded || locked}
                    onChange={() => toggle(item.key as SeedKey)}
                  />
                }
                label={
                  <Link
                    href={githubLink(definition?.line ?? 0)}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                  >
                    {definition?.label ?? item.key}
                  </Link>
                }
              />
              <Typography
                variant="labelSmall"
                sx={{ color: theme => theme.brandPalette.foreground.neutralFaded }}
              >
                {item.seeded
                  ? `Seeded${item.seededAt != null ? ` · ${new Date(item.seededAt).toLocaleString()}` : ''}`
                  : 'Not seeded'}
              </Typography>
            </Box>
          );
        })}
      </Stack>

      <Box sx={{ mt: 4 }}>
        <Button
          variant="primary"
          disabled={locked || selectedKeys.length === 0}
          onClick={() => setConfirmOpen(true)}
        >
          {loading || submitting
            ? 'Starting…'
            : inProgress
              ? 'Seeding…'
              : `Seed selected${selectedKeys.length > 0 ? ` (${selectedKeys.length})` : ''}`}
        </Button>
      </Box>

      <Dialog
        open={confirmOpen}
        onClose={() => !loading && setConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: 'text.secondary', fontWeight: 600 }}>
          Seed default data
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3}>
            <DialogContentText sx={{ fontSize: '0.875rem' }}>
              This will seed the following into the account's primary organisation. It is{' '}
              <strong>irreversible</strong> and each set can only be seeded once:
            </DialogContentText>
            <Box component="ul" sx={{ pl: 3, m: 0 }}>
              {selectedLabels.map(label => (
                <Typography
                  key={label}
                  component="li"
                  sx={{ color: 'text.secondary' }}
                  variant="body2"
                >
                  {label}
                </Typography>
              ))}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="secondary" disabled={loading} onClick={() => setConfirmOpen(false)}>
            Cancel
          </Button>
          <Button variant="primary" disabled={loading} onClick={handleConfirm}>
            {loading ? 'Starting…' : 'Seed now'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

const seedStatusQuery = gql<AccountSeedDefaultsStatusGQL>`
  query AccountSeedDefaultsStatus($accountId: ID!) {
    accountSeedDefaults(accountId: $accountId) {
      accountId
      inProgress
      items {
        key
        seeded
        seededAt
      }
    }
  }
`;

const seedRunMutation = gql<AccountSeedDefaultsRunGQL>`
  mutation AccountSeedDefaultsRun($accountId: ID!, $keys: [AccountSeedKey!]!) {
    accountSeedDefaultsRun(accountId: $accountId, keys: $keys) {
      id
      type
      status
    }
  }
`;
